import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "../Firebase";
import { collection, getDocs } from "firebase/firestore";
import {
  FiActivity,
  FiCheckCircle,
  FiCpu,
  FiArrowRight,
  FiHeart,
  FiSmile,
  FiShield,
  FiUser,
  FiGrid,
  FiSearch,
} from "react-icons/fi";
import { gsap } from "gsap";
import "./DepartmentPage.css";

// Helper to map department IDs to professional medical icons
const getDepartmentIcon = (id) => {
  switch (id) {
    case "orthopedic-trauma":
      return <FiActivity className="sidebar-icon-svg" />;
    case "obgyn":
      return <FiHeart className="sidebar-icon-svg" />;
    case "general-surgery":
      return <FiActivity className="sidebar-icon-svg" />;
    case "plastic-surgery":
      return <FiSmile className="sidebar-icon-svg" />;
    case "laparoscopic-surgery":
      return <FiCpu className="sidebar-icon-svg" />;
    case "advanced-orthopedics":
      return <FiShield className="sidebar-icon-svg" />;
    case "infertility":
      return <FiUser className="sidebar-icon-svg" />;
    case "high-risk-obstetrics":
      return <FiHeart className="sidebar-icon-svg" />;
    case "urology":
      return <FiActivity className="sidebar-icon-svg" />;
    case "oncology":
      return <FiActivity className="sidebar-icon-svg" />;
    case "preventive-health":
      return <FiGrid className="sidebar-icon-svg" />;
    case "cardiology":
      return <FiHeart className="sidebar-icon-svg" />;
    case "internal-medicine":
      return <FiUser className="sidebar-icon-svg" />;
    default:
      return null;
  }
};

const DepartmentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [servicesList, setServicesList] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(
    location.state?.departmentId || null,
  );
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const detailRef = useRef(null);

  // Fetch from Firestore
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "services"));
        if (!querySnapshot.empty) {
          const list = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setServicesList(list);

          // Select default if not selected yet
          if (location.state?.departmentId) {
            setSelectedDepartmentId(location.state.departmentId);
          } else {
            setSelectedDepartmentId(list[0].id);
          }
        }
      } catch (err) {
        console.error("Error fetching services from Firestore: ", err);
      }
    };
    fetchServices();
  }, [location.state]);

  useEffect(() => {
    if (location.state?.departmentId) {
      setSelectedDepartmentId(location.state.departmentId);
      setIsHighlighted(true);

      // Auto-scroll to the details section after render
      setTimeout(() => {
        const container = document.querySelector(".services-container");
        if (container) {
          container.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 150);

      const timer = setTimeout(() => {
        setIsHighlighted(false);
      }, 3000); // Outline stays active for 3 seconds

      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Smooth transition when changing departments
  useEffect(() => {
    if (detailRef.current) {
      gsap.fromTo(
        detailRef.current,
        { opacity: 0, y: 15 },
        { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" },
      );
    }
  }, [selectedDepartmentId]);

  const selectedDepartment =
    servicesList.find((dept) => dept.id === selectedDepartmentId) ||
    servicesList[0] ||
    null;

  // On mobile/tablet the sidebar sits above the detail content, so selecting
  // a service should redirect/scroll the user down to that service's info.
  const handleSelectDepartment = (id) => {
    setSelectedDepartmentId(id);
    if (window.innerWidth <= 1024) {
      setTimeout(() => {
        const detailEl = document.querySelector(".service-detail-card");
        if (detailEl) {
          detailEl.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  };

  const filteredServices = servicesList.filter(
    (dept) =>
      dept.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dept.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Auto-select first matching service when searching
  useEffect(() => {
    if (searchTerm && filteredServices.length > 0) {
      setSelectedDepartmentId(filteredServices[0].id);
    }
  }, [searchTerm, filteredServices]);

  return (
    <div className="services-page-wrapper">
      <header
        className="services-header"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(10, 36, 34, 0.52) 0%, rgba(10, 36, 34, 0.62) 100%), url(${process.env.PUBLIC_URL}/assets/images/img17.png)`,
        }}
      >
        <div className="services-header-overlay" />
        <div className="services-header-content">
          <h1>Services & Specialties</h1>
          <p>
            Explore the departments and services that support you and your
            family, from everyday care to complex treatments.
          </p>
        </div>
      </header>

      {/* Search Bar Below Banner */}
      <div className="services-search-section">
        <div className="services-search-box">
          <FiSearch className="services-search-icon" />
          <input
            type="text"
            placeholder="Search services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="services-search-input"
          />
        </div>
      </div>

      <div className="services-container">
        {servicesList.length === 0 && (
          <div
            style={{ padding: "60px", textAlign: "center", color: "#64748b" }}
          >
            <div style={{ fontSize: "2rem", marginBottom: "10px" }}>⏳</div>
            <p>Loading services...</p>
          </div>
        )}
        {servicesList.length > 0 && (
          <>
            {/* Sidebar Navigation */}
            <aside className="services-sidebar">
              <h3>Centres of Excellence</h3>
              <div className="sidebar-scroll-container">
                {filteredServices.map((dept) => {
                  const professionalIcon = getDepartmentIcon(dept.id);
                  const isActive = selectedDepartmentId === dept.id;
                  return (
                    <button
                      key={dept.id}
                      className={`dept-nav-item ${isActive ? "active" : ""}`}
                      onClick={() => handleSelectDepartment(dept.id)}
                    >
                      <span className="dept-nav-icon">
                        {professionalIcon ? professionalIcon : dept.icon}
                      </span>
                      <span className="dept-nav-text">{dept.name}</span>
                    </button>
                  );
                })}
                {filteredServices.length === 0 && (
                  <div
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#64748b",
                    }}
                  >
                    <p>No services found matching "{searchTerm}"</p>
                  </div>
                )}
              </div>
            </aside>

            {/* Detail Content */}
            <main
              className={`service-detail-card ${isHighlighted ? "highlight-active" : ""}`}
              ref={detailRef}
            >
              <div className="detail-header">
                <h2>{selectedDepartment.name}</h2>
                <div className="header-divider" />
                <p className="detail-subtitle">
                  {selectedDepartment.description}
                </p>
              </div>

              <div className="detail-body-grid">
                <div className="detail-section">
                  <h3>
                    <FiActivity className="section-title-icon" /> Overview
                  </h3>
                  <p className="overview-text">
                    {selectedDepartment.overview || (
                      <>
                        Our {selectedDepartment.name} department is dedicated to
                        providing comprehensive care using the latest medical
                        advancements. We focus on accurate diagnosis,
                        personalized treatment plans, and holistic recovery to
                        ensure the best outcomes for our patients.
                      </>
                    )}
                  </p>
                </div>

                {selectedDepartment.images &&
                  selectedDepartment.images.length > 0 && (
                    <div
                      className="detail-section full-width"
                      style={{ marginTop: "30px" }}
                    >
                      <h3>
                        <FiCheckCircle className="section-title-icon" /> Gallery
                      </h3>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(300px, 1fr))",
                          gap: "20px",
                          marginTop: "15px",
                          justifyItems: "center",
                        }}
                      >
                        {selectedDepartment.images.map((imgUrl, idx) => (
                          <img
                            key={idx}
                            src={imgUrl}
                            alt={`${selectedDepartment.name} facility ${idx + 1}`}
                            style={{
                              width: "100%",
                              maxWidth: "400px",
                              height: "300px",
                              objectFit: "cover",
                              background: "#ffffff",
                              borderRadius: "12px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                <div className="detail-section">
                  <h3>
                    <FiCheckCircle className="section-title-icon" /> Key
                    Treatments & Procedures
                  </h3>
                  <ul className="detail-list">
                    {selectedDepartment.keyTreatments ? (
                      selectedDepartment.keyTreatments.map(
                        (treatment, index) => (
                          <li key={index}>
                            <span className="list-bullet">✓</span>
                            <span className="list-text">{treatment}</span>
                          </li>
                        ),
                      )
                    ) : (
                      <>
                        <li>
                          <span className="list-bullet">✓</span>
                          <span className="list-text">
                            Advanced diagnostic screenings and assessments
                          </span>
                        </li>
                        <li>
                          <span className="list-bullet">✓</span>
                          <span className="list-text">
                            Minimally invasive surgical procedures
                          </span>
                        </li>
                        <li>
                          <span className="list-bullet">✓</span>
                          <span className="list-text">
                            Comprehensive rehabilitation and follow-up care
                          </span>
                        </li>
                        <li>
                          <span className="list-bullet">✓</span>
                          <span className="list-text">
                            24/7 emergency support for related conditions
                          </span>
                        </li>
                      </>
                    )}
                  </ul>
                </div>

                <div className="detail-section full-width">
                  <h3>
                    <FiCpu className="section-title-icon" /> Technology &
                    Facilities
                  </h3>
                  <p className="tech-text">
                    We are equipped with state-of-the-art technology to support
                    our specialists. From high-resolution imaging to
                    robotic-assisted surgery systems, we invest in tools that
                    improve precision and safety. Our operating theaters and
                    diagnostic wings are designed to support rapid recovery. Our
                    specialists utilize these resources to tailor treatments
                    specifically for you.
                  </p>
                </div>
              </div>

              <div className="cta-box">
                <div className="cta-box-glow" />
                <div className="cta-content">
                  <h4>
                    Need to see a specialist in {selectedDepartment.name}?
                  </h4>
                  <p>
                    Our team of expert consultants is ready to help you on your
                    path to recovery.
                  </p>
                </div>
                <button
                  className="cta-btn"
                  onClick={() =>
                    navigate("/appointments", {
                      state: { specialty: selectedDepartment.name },
                    })
                  }
                >
                  Book an Appointment <FiArrowRight className="cta-arrow" />
                </button>
              </div>
            </main>
          </>
        )}
      </div>
    </div>
  );
};

export default DepartmentPage;
